import React, { useCallback } from 'react';

import jschardet from 'jschardet';
import Papa from 'papaparse';

import { csvDelimiters } from '@qality/common-config';
import { ParsedFile } from '@qality/common-model';
import { FlagService } from '@qality/common-service';

import DropzoneWithFiles from './DropzoneWithFiles';

type Props = {
  file: ParsedFile;
  onChange: (event: React.ChangeEvent | ParsedFile) => void;
  onDelimiterChange: (event: React.ChangeEvent) => void;
  onEncodingChange: (event: React.ChangeEvent | string) => void;
};

export default function CsvDropzoneWithFiles({
  file,
  onChange,
  onDelimiterChange: onDelimiterChange,
  onEncodingChange,
}: Props): JSX.Element {
  const getOnLoadCallback = useCallback(
    (reader, rawFile: any) => {
      return () => {
        const { encoding: detectedEncoding } = jschardet.detect(reader.result);
        onEncodingChange(detectedEncoding);

        reader.onload = () => {
          const newFile = new ParsedFile(rawFile.name, rawFile.size);
          newFile.content = reader.result.trim();
          onChange(newFile);

          const csvData = Papa.parse(newFile.content, {
            header: true,
          });

          updateDelimiter(csvData.meta.delimiter, onDelimiterChange);
        };

        reader.readAsText(rawFile, detectedEncoding);
      };
    },
    [onChange, onDelimiterChange, onEncodingChange]
  );

  const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      validate(fileRejections);
      acceptedFiles.forEach((acceptedFile) => {
        const reader = new FileReader();
        reader.onabort = onAbort;
        reader.onerror = onError;
        reader.onload = getOnLoadCallback(reader, acceptedFile);
        reader.readAsBinaryString(acceptedFile);
      });
    },
    [getOnLoadCallback]
  );

  const removeFile = () => {
    onChange(null);
  };

  return (
    <DropzoneWithFiles file={file} onRemoveFile={removeFile} onDrop={onDrop} />
  );
}

function validate(fileRejections) {
  if (fileRejections.length) {
    FlagService.createErrorFlag(
      'Upload unsuccessful',
      'We couldn’t upload the selected file. ' +
        'Make sure that the file you are trying to upload is in CSV format.'
    );
  }
}

function onAbort() {
  FlagService.createErrorFlag(
    'Upload unsuccessful',
    'We couldn’t upload the selected file. File reading was aborted.'
  );
}

function onError() {
  FlagService.createErrorFlag(
    'Upload unsuccessful',
    'We couldn’t upload the selected file. File reading has failed.'
  );
}

function updateDelimiter(delimiterValue, onDelimiterChange) {
  const delimiterOption = csvDelimiters.find((d) => d.value === delimiterValue);
  if (delimiterOption) {
    onDelimiterChange(delimiterOption);
  } else {
    onDelimiterChange({ label: delimiterValue, value: delimiterValue });
  }
}
